import { logRuntimeDiagnostic } from "../diagRuntime";

const reloadGuardPrefix = "__timed_reload_guard__";

/**
 * Un chunk a-t-il echoue a se charger, ou est-ce une erreur ordinaire ?
 *
 * ## LA PILE N'EST PAS UN INDICE, C'EST UN PIEGE
 *
 * Cette fonction lisait `error.stack`. Or **toute** erreur levee dans une page
 * chargee paresseusement a une pile qui mentionne `.chunk.js` — c'est la que
 * vit son code. Un `TypeError` banal dans un ecran devenait donc un « chunk
 * load error ».
 *
 * La consequence se voit mal, parce qu'elle ne ressemble pas a un defaut de
 * detection : l'ecran annonce « Mise a jour de l'application », recharge,
 * retombe sur le meme bug, et le garde-fou — ayant consomme son unique essai —
 * fige ce message. On croit a un probleme de deploiement pendant qu'un ecran
 * plante.
 *
 * Verifie le 27/08/2026 sur un `TypeError` dont la pile passait par
 * `517.b2c1d3e4.chunk.js` : classe « chunk » a tort.
 *
 * On ne regarde donc que le **nom** et le **message**, qui disent ce qui s'est
 * produit, jamais la pile, qui dit seulement ou.
 *
 * `\.chunk\.(js|css)` seul disparait pour la meme raison : trop large des lors
 * qu'un message cite un fichier. Les navigateurs qui nomment le fichier le font
 * dans un message qui commence deja par « Loading chunk N failed ».
 */
export function isRecoverableChunkLoadError(error: unknown, detail = "") {
    const errorDetail = error instanceof Error
        ? `${error.name}:${error.message}`
        : typeof error === "string"
            ? error
            : "";
    const text = `${detail}\n${errorDetail}`;

    return /ChunkLoadError|Loading chunk \d+ failed|Loading CSS chunk \d+ failed|Failed to fetch dynamically imported module|Importing a module script failed|Unexpected token '<'/i.test(text);
}

/**
 * Recharger en allant vraiment rechercher le document.
 *
 * `location.reload()` ne contourne pas le cache HTTP du document : le
 * navigateur peut reservir le meme `index.html`. Or c'est precisement lui le
 * probleme quand un chunk manque — il nomme des fichiers qui n'existent plus.
 * Recharger sans le renouveler rejoue donc la meme panne, et le garde-fou,
 * ayant deja consomme son unique essai, laisse l'ecran bloque.
 *
 * `fetch(..., {cache: "reload"})` force la revalidation du document et met le
 * frais dans le cache. Le rechargement qui suit part alors du bon `index.html`.
 *
 * On n'ajoute pas de parametre d'anti-cache a l'adresse : il resterait dans la
 * barre, dans les favoris et dans les partages.
 */
export async function rechargerEnRenouvelantLeDocument(): Promise<void> {
    try {
        await fetch(window.location.href, {cache: "reload", credentials: "same-origin"});
    } catch {
        // Hors ligne, ou document injoignable : on recharge quand meme. Sans
        // reseau, il n'y a de toute facon rien de mieux a tenter.
    }
    window.location.reload();
}

export function reloadPageOnce(reason: string) {
    const key = `${reloadGuardPrefix}${reason}`;
    let guardPersisted = false;

    try {
        if (window.sessionStorage.getItem(key) === "1") {
            console.warn(`[reload-guard] reload already performed for ${reason}`);
            void logRuntimeDiagnostic("reload-skip", reason, { reason, guard: "already-performed" });
            return false;
        }

        window.sessionStorage.setItem(key, "1");
        guardPersisted = true;
    } catch (error) {
        console.warn("[reload-guard] sessionStorage unavailable", error);
        void logRuntimeDiagnostic("reload-skip", reason, {
            reason,
            guard: "session-storage-unavailable",
            name: error instanceof Error ? error.name : null,
        });
    }

    if (!guardPersisted) {
        return false;
    }

    void logRuntimeDiagnostic("reload-start", reason, { reason });
    window.setTimeout(() => { void rechargerEnRenouvelantLeDocument(); }, 100);
    return true;
}
